import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SelectBox from 'devextreme-react/select-box';
import { Button } from 'devextreme-react/button';
import { Popup } from 'devextreme-react';
import ArrayStore from 'devextreme/data/array_store';
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import { Row } from 'react-bootstrap';

const order_day_panel = (params: any) => {
  const dataGridRef = useRef<any>(null);
  const [popupVisible, setPopupVisible] = useState(false);

  const [data, setData] = useState(() => {
    const initial = [];
    for (let i = 1; i <= 7; i++) {
      initial.push({
        id: i,
        isOrderDay: params.data?.['isOrderDay0' + i] ?? false,
        deliveryDay: params.data?.['deliveryDay0' + i] ?? 0,
      });
    }
 
    return initial;
  });

  const [selectionWeekPeriod, setSelectionWeekPeriod] = useState<number | null>(params.data?.weekPeriod ?? null);

useEffect(() => {
  if (popupVisible) {
    if (params.data?.orderPeriod === 'W') {
      setSelectionWeekPeriod(params.data.weekPeriod ?? null);
    }

    const updatedData = [];
    for (let i = 1; i <= 7; i++) {
      updatedData.push({
        id: i,
        isOrderDay: params.data?.['isOrderDay0' + i] ?? false,
        deliveryDay: params.data?.['deliveryDay0' + i] ?? 0,
      });
    }
    setData(updatedData);
  }
}, [popupVisible, params.data]);

  const weekPeriods = [
    { id: 0, description:'0' },
    { id: 1, description: '1' },
    { id: 2, description: '2' },
    { id: 3, description: '3' },
    { id: 4, description: '4' },
  ];

  const locationStore = useMemo(() => new ArrayStore({ data }), [data]);

  const toggleIsOrderDay = useCallback(
    (id: number) => {
      setData((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isOrderDay: !item.isOrderDay } : item
        )
      );
    },
    [setData]
  );

  const onDeliveryDayChange = useCallback(
    (id: number, newValue: number) => {
      setData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, deliveryDay: newValue } : item))
      );
    },
    [setData]
  );

  const onPopupHiding = () => {
    const grid = dataGridRef.current?.instance;
    if (!grid) {
      setPopupVisible(false);
      return;
    }

    const changes = [];
    for (const item of data) {
      const i = item.id;
      const isOrderDayKey = 'isOrderDay0' + i;
      const deliveryDayKey = 'deliveryDay0' + i;

      const originalIsOrderDay = params.data[isOrderDayKey];
      const originalDeliveryDay = params.data[deliveryDayKey];

      const changedFields: Record<string, any> = {};

      if (originalIsOrderDay !== item.isOrderDay) {
        changedFields[isOrderDayKey] = item.isOrderDay;
      }
      if (originalDeliveryDay !== item.deliveryDay) {
        changedFields[deliveryDayKey] = item.deliveryDay;
      }

      if (Object.keys(changedFields).length > 0) {
        changes.push({
          key: params.key ?? params.data.id,
          type: 'update',
          data: changedFields,
        });

        Object.entries(changedFields).forEach(([field, value]) => {
          params.component.cellValue(params.rowIndex, field, value);
          params.data[field] = value;
        });
      }
    }

    if (params.data.weekPeriod !== selectionWeekPeriod) {
      changes.push({
        key: params.key ?? params.data.id,
        type: 'update',
        data: { weekPeriod: selectionWeekPeriod },
      });
      params.component.cellValue(params.rowIndex, 'weekPeriod', selectionWeekPeriod);
      params.data.weekPeriod = selectionWeekPeriod;
    }

    if (changes.length > 0) {
      const existingChanges = grid.option('editing.changes');
      const safeExistingChanges = Array.isArray(existingChanges) ? existingChanges : [];
      grid.option('editing.changes', [...safeExistingChanges, ...changes]);

      params.component.cellValue(params.rowIndex, 'summary', Date.now());
    }

    setPopupVisible(false);
  };

  const headers = [
    ...data.map((row) => (
      <td key={row.id} style={{ padding: '2px 2px' }}>
        {t('syncflow:orderday_0' + row.id)}
      </td>
    )),
    <td key="week-period" style={{ padding: '2px 2px' }}>
      {t('syncflow:short_week_period')}
    </td>,
  ];

  const vals = [
    ...data.map((row) => (
      <td key={row.id} style={{ textAlign: 'center' }}>
        {row.isOrderDay && row.deliveryDay >= 0 ? (
          <span className="badge badge-pill badge-outline badge-primary">{row.deliveryDay}</span>
        ) : (
          ''
        )}
      </td>
    )),
    <td key="week-period-value" style={{ textAlign: 'center' }}>
      {selectionWeekPeriod != null && selectionWeekPeriod > 0 ? (
        <span className="badge badge-pill badge-outline badge-secondary">{selectionWeekPeriod}</span>
      ) : (
        ''
      )}
    </td>,
  ];

  return (
    <>
      <table>
        <thead>
          <tr>
            {headers}
            <td style={{ padding: '2px 15px' }} rowSpan={2}>
              <Button style={{ margin: '2px 0px' }} icon="more" onClick={() => setPopupVisible(true)} />
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>{vals}</tr>
        </tbody>
      </table>

      <Popup
        visible={popupVisible}
        onHiding={onPopupHiding}
        showCloseButton={true}
        showTitle={true}
        width={600}
        height={500}
        position={{ of: window, at: 'center', my: 'center' }}
        title={t('syncflow:order_days')}
      >
        <div className="p-4" style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <label style={{ width: 150, marginRight: 8 }}>{t('syncflow:week_period')}</label>
          <SelectBox
            dataSource={weekPeriods}
            displayExpr="description"
            valueExpr="id"
            showClearButton
            onValueChange={(val: number | null) => setSelectionWeekPeriod(val)}
            value={selectionWeekPeriod}
            width={200}
          />
        </div>

        <DataGridWrapper
          height={350}
          props={{ ref: dataGridRef }}
          datasource={locationStore}
          searchPanelVisible={false}
          columnChooser={false}
          editingConfig={{
            mode: 'batch',
            useIcons: false,
            allowAdding: false,
            allowDeleting: false,
            allowUpdating: false,
          }}
          columns={[
            {
              dataField: 'id',
              caption: t('syncflow:lb_day'),
              allowEditing: false,
              alignment: 'left',
              cellRender: (data: any) => t('syncflow:delivery_0' + data.data.id),
            },
            {
              dataField: 'isOrderDay',
              caption: t('syncflow:lu_is_order_day'),
              allowEditing: false,
              alignment: 'center',
              cellRender: (cellData: any) => (
                <input
                  type="checkbox"
                  checked={cellData.data.isOrderDay}
                  onChange={() => toggleIsOrderDay(cellData.data.id)}
                  style={{cursor: 'pointer'}}
                />
              ),
            },
            {
              dataField: 'deliveryDay',
              caption: t('syncflow:delivery_day'),
              alignment: 'center',
              allowEditing: false,
              cellRender: (cellData: any) => (
                <input
                  type="number"
                  max={7}
                  min={0}
                  value={cellData.data.deliveryDay ?? ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) onDeliveryDayChange(cellData.data.id, val);
                  }}
                  style={{ padding: '4px', boxSizing: 'border-box',width: '60px' }}
                />
              ),
            },
          ]}
        />
      </Popup>
    </>
  );
};

export default order_day_panel;
